import React from "react"

import { hasUnsyncedChanges } from "@nozbe/watermelondb/sync"
import { t } from "i18next"

import { DisplayableError } from "@treefort/lib/displayable-error"
import { WatermelonError } from "@treefort/lib/errors"

import Column from "../../../../components/column"
import { PlainSettingsButton } from "../../../../components/plain-settings-button"
import SettingsFieldset from "../../../../components/settings-fieldset"
import SettingsRow from "../../../../components/settings-row"
import { SettingsUpdates } from "../../../../components/settings-updates"
import config from "../../../../config"
import confirm from "../../../../lib/confirm"
import { logError } from "../../../../lib/logging"
import { toast } from "../../../../lib/toaster"
import { getClient } from "../../../../watermelon/db"
import { syncManager } from "../../../../watermelon/sync"
import MenuLayout from "../../../layouts/menu"

async function onPressDeleteLocalData() {
  // We will default to false because if we are unable to check for local
  // changes (i.e. hasUnsyncedChanges throws) then it indicates a corrupt
  // database of some kind and we won't be able to do anything about it anyway.
  let unsyncedChangesPresent = false

  try {
    unsyncedChangesPresent = await hasUnsyncedChanges({
      database: getClient(),
    })
  } catch (cause) {
    logError(
      new WatermelonError(
        "Error while checking for unsynced changes during user-initiated local data deletion",
        { cause },
      ),
    )
  }

  const message = unsyncedChangesPresent
    ? t(
        "Warning: you have local data that has not yet been synced to the server. To save it, cancel this action and sync local changes first. If you continue now, this data will be lost. Do you want to proceed to delete local data?",
      )
    : t("Are you sure you want to delete local data?")

  const response = await confirm({
    title: t("Delete?"),
    message,
    cancelLabel: t("Cancel"),
    confirmLabel: t("Delete"),
  })

  if (response) {
    try {
      await syncManager.resetDatabase()
      toast.success(t("Deleted local data."))
    } catch (cause) {
      throw new DisplayableError(
        t("An error occurred. Please contact us if the issue persists."),
        new WatermelonError("Failed to reset database (user-initiated)", {
          cause,
        }),
      )
    }
  }
}

export function AdvancedSettingsScreen(): React.JSX.Element {
  return (
    <MenuLayout>
      <Column gap="large" alignItems="stretch">
        <SettingsUpdates />
        <SettingsFieldset title={t("Local data")}>
          <SettingsRow justifyContent="center" paddingVertical="none">
            <PlainSettingsButton
              id="adv-settings-sync-local-data"
              onPress={async () => {
                try {
                  await syncManager.requestSync({ syncType: "immediate" })
                  toast.success(t("Sync complete"))
                } catch (cause) {
                  throw new DisplayableError(
                    t(
                      "An error occurred. Please contact us if the issue persists.",
                    ),
                    new WatermelonError("User-initiated sync failed", {
                      cause,
                    }),
                  )
                }
              }}
            >
              {t("Sync local data")}
            </PlainSettingsButton>
          </SettingsRow>
          <SettingsRow
            justifyContent="center"
            paddingVertical="none"
            showBorder
          >
            <PlainSettingsButton
              id="adv-settings-delete-local-data"
              color="danger"
              onPress={onPressDeleteLocalData}
            >
              {t("Delete local data")}
            </PlainSettingsButton>
          </SettingsRow>
        </SettingsFieldset>
        {config.ENV === "development" ? (
          // eslint-disable-next-line @shopify/jsx-no-hardcoded-content
          <SettingsFieldset title="Development">
            <SettingsRow justifyContent="center" paddingVertical="none">
              {/* eslint-disable-next-line @shopify/jsx-no-hardcoded-content */}
              <PlainSettingsButton
                id="adv-settings-test-intentional-test-error"
                onPress={() => {
                  throw new Error("[Test] Intentional test error", {
                    cause: new Error("Someone tapped the 'Throw error' button"),
                  })
                }}
              >
                Throw error
              </PlainSettingsButton>
            </SettingsRow>
          </SettingsFieldset>
        ) : null}
      </Column>
    </MenuLayout>
  )
}
